import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSetRecoilState } from 'recoil';

import { TeamTaskAssignmentReq, teamApi } from '../api';
import { TEAM_ASSIGNED_TASKS_QUERY_KEY, TEAM_TASKS_QUERY_KEY } from '../queries';
import { eventsSnackBarAtom } from '../recoil';
import { translateI18n } from '../../i18n';

interface UpdateTeamTaskAssignmentParams {
  teamId: string;
  boardId: string;
  taskId: string;
  teamTaskAssignmentReq: TeamTaskAssignmentReq;
  showSnackbar?: boolean;
}

export const useUpdateTeamTaskAssignment = () => {
  const queryClient = useQueryClient();
  const setEventsSnackbar = useSetRecoilState(eventsSnackBarAtom);

  const updateTeamTaskAssignmentMutation = useMutation({
    mutationFn: ({
      teamId,
      boardId,
      taskId,
      teamTaskAssignmentReq,
    }: UpdateTeamTaskAssignmentParams) => {
      return teamApi
        .updateTeamTaskAssignment(teamId, boardId, taskId, teamTaskAssignmentReq)
        .then(({ data }) => data);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: TEAM_TASKS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: TEAM_ASSIGNED_TASKS_QUERY_KEY });

      if (variables.showSnackbar !== false) {
        setEventsSnackbar({ message: translateI18n('team.tasks.snackbar.updated'), type: 'success', icon: 'task' });
      }
    },
    onError: (_error, variables) => {
      if (variables.showSnackbar !== false) {
        setEventsSnackbar({ message: translateI18n('team.tasks.snackbar.assignmentUpdateFailed'), type: 'error', icon: 'task' });
      }
    },
  });

  return {
    updateTeamTaskAssignment: updateTeamTaskAssignmentMutation.mutateAsync,
    isUpdatingTeamTaskAssignment: updateTeamTaskAssignmentMutation.isPending,
    ...updateTeamTaskAssignmentMutation,
  };
};
